import { Injectable } from '@angular/core';
import { Observable, of, delay, map } from 'rxjs';

import { environment } from '../../../../../../environments/environment';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { BillingData, Doctor, Patient, PaymentMethod, Study } from '../../models/dtos/order-stateDto';

@Injectable({
    providedIn: 'root'
})
export class NewOrderService {
    private apitype = environment.apiUrl + 'settings/catalogs/types';
    private apiUrl = environment.apiUrl;
    private headers!: HttpHeaders;

    private patients: Patient[] = [];
    private studies: Study[] = [];
    private doctors: Doctor[] = [];
    private paymentMethods: PaymentMethod[] = [
        {
            idPaymentMethod: 1,
            name: 'Efectivo',
            code: 'CASH',
            requiresReference: false,
            requiresCardData: false
        },
        {
            idPaymentMethod: 2,
            name: 'Tarjeta de Débito',
            code: 'DEBIT_CARD',
            requiresReference: true,
            requiresCardData: true
        },
        {
            idPaymentMethod: 3,
            name: 'Tarjeta de Crédito',
            code: 'CREDIT_CARD',
            requiresReference: true,
            requiresCardData: true
        },
        {
            idPaymentMethod: 4,
            name: 'Transferencia Bancaria',
            code: 'BANK_TRANSFERENCY',
            requiresReference: true,
            requiresCardData: false
        },
        {
            idPaymentMethod: 5,
            name: 'QR',
            code: 'QR',
            requiresReference: true,
            requiresCardData: true
        },
        {
            idPaymentMethod: 6,
            name: 'Cheque',
            code: 'CHECK',
            requiresReference: true,
            requiresCardData: false
        }
    ];

    constructor(private http: HttpClient) {}

    // ============================================
    // MÉTODOS DE PACIENTES
    // ============================================

    searchPatientByCi(type: string, value?: string, from?: string, to?: string): Observable<any> {
        let params: any = { type };
        if (value) params.value = value;
        if (from) params.from = from;
        if (to) params.to = to;

        // Retornar el Observable directamente
        return this.http.get<any>(this.apiUrl + 'patients/search', { params });
    }

    getAllPatients(): Observable<Patient[]> {
        return of(this.patients).pipe(delay(300));
    }

    createPatient(patient: Patient): Observable<Patient> {
        const newPatient = {
            ...patient,
            idPatient: this.patients.length + 1
        };
        this.patients.push(newPatient);
        return of(newPatient).pipe(delay(500));
    }

    // ============================================
    // MÉTODOS DE SEGUROS
    // ============================================

    getAllSecures(): Observable<any> {
        return this.http.get<any>(this.apiUrl + 'secures/list');
    }

    getSecureSubsidiaries(idSecure: number): Observable<any> {
        return this.http.get<any>(this.apiUrl + 'secures/listSubsidiaries', {
            params: { idSecure: idSecure.toString() }
        });
    }

    // ============================================
    // MÉTODOS DE ESTUDIOS
    // ============================================

    getAllStudies(): Observable<Study[]> {
        // return of(this.studies).pipe(delay(300));
        return this.http.get<any>(this.apiUrl + `settings/studies/listStudy`);
    }

    getAllStudiesLaboratory(): Observable<Study[]> {
        // return of(this.studies).pipe(delay(300));
        return this.http.get<any>(this.apiUrl + `settings/studies/listLabStudy`);
    }

    getStudiesByCategory(idCategory: number): Observable<any> {
        return this.http.get<any>(this.apiUrl + `settings/studies/category/${idCategory}/list`);
    }

    getCombo(idStudy: number, idSecureType: number): Observable<any> {
        return this.http.get<any>(this.apiUrl + `settings/studies/combo/${idStudy}/${idSecureType}`);
    }

    // #region MÉTODOS DE PRECIOS

    getStudyPrice(idStudy: number, idDeliveryType: number, idSecureType: number): Observable<any> {
        // TODO: Reemplazar con el endpoint real cuando esté disponible
        return this.http.get<any>(`${this.apiUrl}settings/studies/studyPrice/${idStudy}/${idSecureType}/${idDeliveryType}`);
    }

    getStudyPriceCombo(idStudy: number, idSecureType: number): Observable<any> {
        // TODO: Reemplazar con el endpoint real cuando esté disponible
        return this.http.get<any>(`${this.apiUrl}settings/studies/combo/${idStudy}/${idSecureType}`);
    }

    // #region TIPOS DE ENTREGA

    getAllDeliveryTypes(): Observable<any> {
        return this.http.get<any>(this.apiUrl + 'settings/catalogs/types/category/DELIVERY_TYPE/list');
    }

    // #region DOCTORES

    searchDoctorByRegistration(type: string, value?: string, from?: string, to?: string): Observable<Doctor | null> {
        let params: any = { type };
        if (value) params.value = value;
        if (from) params.from = from;
        if (to) params.to = to;
        return this.http.get<Doctor>(this.apiUrl + 'doctors/search', { params }); // Simular delay de red
    }

    searchDoctorsByQuery(query: string): Observable<Doctor | null> {
        const params = { query };
        return this.http.get<Doctor>(this.apiUrl + 'doctors/search/query', { params, headers: this.headers });
    }

    getAllDoctors(): Observable<Doctor[]> {
        return of(this.doctors).pipe(delay(300));
    }

    createDoctor(doctor: Doctor): Observable<Doctor> {
        const newDoctor = {
            ...doctor,
            idDoctor: this.doctors.length + 1
        };
        this.doctors.push(newDoctor);
        return of(newDoctor).pipe(delay(500));
    }

    // ============================================
    // MÉTODOS DE FACTURACIÓN
    // ============================================

    getBillingDataByPatient(idPatient: number): Observable<BillingData | null> {
        // Simular que algunos pacientes tienen datos de facturación
        if (idPatient === 1) {
            return of({
                idBillingAccount: 1,
                companyName: 'Empresa XYZ S.A.',
                ruc: '80012345-6',
                dv: '6',
                email: 'facturacion@empresa.com',
                phone: '+595211234567',
                address: 'Av. España 1000, Asunción'
            }).pipe(delay(300));
        }

        return of(null).pipe(delay(300));
    }

    saveBillingData(billingData: BillingData): Observable<BillingData> {
        const saved = {
            ...billingData,
            idBillingAccount: billingData.idBillingAccount || 100
        };
        return of(saved).pipe(delay(500));
    }

    // ============================================
    // MÉTODOS DE MÉTODOS DE PAGO
    // ============================================

    getAllPaymentMethods(): Observable<PaymentMethod[]> {
        return of(this.paymentMethods).pipe(delay(200));
    }

    // ============================================
    // CATEGORÍAS DE ESTUDIOS
    // ============================================

    getStudyCategories(): Observable<any> {
        return this.http.get<any>(this.apiUrl + 'settings/categories/list');
    }

    // #region TYPE/STATUS/ESTUDY

    gettype(category: string): Observable<any> {
        return this.http.get<any>(this.apitype + '/category/' + category + '/list', {
            headers: this.headers
        });
    }

    createOrder(orderType: 0 | 1, orderDTO: any): Observable<any> {
        return this.http.post<any>(`${this.apiUrl}orders?orderType=${orderType}`, orderDTO, {
            headers: this.headers
        });
    }
}
